import { Form, Input } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { ReactComponent as PasswordVisibleSvg } from '../../assets/icons/password-visible.svg';
import { ReactComponent as PasswordHiddenSvg } from '../../assets/icons/password-hide.svg';
import Icon from '@ant-design/icons';
import { sanitizeInput } from '../../utilities/commonFunctions';
import PropTypes from 'prop-types';

const PasswordIconRender = ({ visible }) =>
  visible ? (
    <Icon component={PasswordVisibleSvg} />
  ) : (
    <Icon component={PasswordHiddenSvg} />
  );

PasswordIconRender.propTypes = {
  visible: PropTypes.bool,
};

const CustomFormInput = ({
  label,
  placeholder,
  name,
  rules,
  validateStatus,
  help,
  onChange,
  value,
  readOnly = false,
  width = '100%',
  isPassword = false,
  defaultValue = null,
}) => {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const passwordInputRef = useRef(null);
  const cursorPositionRef = useRef(null);

  const handleChange = e => {
    const sanitizedValue = sanitizeInput(e.target.value);
    onChange?.({ ...e, target: { ...e.target, value: sanitizedValue } });
  };

  const togglePasswordVisibility = () => {
    const inputElement = passwordInputRef.current?.input;

    if (inputElement) {
      cursorPositionRef.current = {
        start: inputElement.selectionStart,
        end: inputElement.selectionEnd,
      };
    }

    setPasswordVisible(prev => !prev);
  };

  useEffect(() => {
    if (!isPassword || !cursorPositionRef.current) {
      return;
    }

    const inputElement = passwordInputRef.current?.input;

    if (!inputElement) {
      return;
    }

    const animationFrameId = window.requestAnimationFrame(() => {
      inputElement.focus({ preventScroll: true });
      inputElement.setSelectionRange(
        cursorPositionRef.current.start,
        cursorPositionRef.current.end
      );
    });

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [isPassword, passwordVisible]);

  return (
    <Form.Item
      className="custom-input"
      name={name}
      rules={rules}
      label={label}
      style={{ width }}
      validateStatus={validateStatus}
      help={help}
    >
      {isPassword ? (
        <Input
          ref={passwordInputRef}
          type={passwordVisible ? 'text' : 'password'}
          autoComplete="off"
          placeholder={placeholder}
          readOnly={readOnly}
          onChange={handleChange}
          value={value}
          suffix={
            <span
              role="button"
              tabIndex={0}
              onMouseDown={event => event.preventDefault()}
              onClick={togglePasswordVisibility}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  togglePasswordVisibility();
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <PasswordIconRender visible={passwordVisible} />
            </span>
          }
        />
      ) : (
        <Input
          autoComplete="off"
          placeholder={placeholder}
          readOnly={readOnly}
          onChange={handleChange}
          value={value}
          {...(defaultValue != null && { defaultValue })}
        />
      )}
    </Form.Item>
  );
};

CustomFormInput.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  placeholder: PropTypes.string,
  name: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  rules: PropTypes.arrayOf(PropTypes.object),
  validateStatus: PropTypes.string,
  help: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onChange: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  readOnly: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isPassword: PropTypes.bool,
  defaultValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object,
  ]),
};

export default React.memo(CustomFormInput);
